import React, { useState } from 'react';
import styled from 'styled-components';
import Markdown from '@common/Node/CompletionChatbot/Markdown';

import { Modal, Button, copyToClipboard } from '@common/index';
import { Alert, ListItemButton, Stack, alpha } from '@mui/material';

import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { flowActions } from '@store/slices';
import { API_BASE_NO_API, AppDispatch, State } from '@store/index';
import { CheckOutlined, CodeOutlined, ContentCopyRounded } from '@mui/icons-material';
import { Label } from '@common/Label';
import { useTheme } from '../../styles';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const CopyEndpointButton = styled.div<{ methodColor?: string }>`
  width: 100%;

  & .MuiListItemButton-root {
    color: ${({ theme }) => theme.text.standard};
    border-radius: 6px;
    font-weight: 500;
    font-size: 1.4ch;
    border: 1px solid ${({ theme }) => theme.line.light};

    height: 50px;

    svg {
      color: ${({ theme, methodColor }) => methodColor || theme.primary};
    }

    &:hover {
      color: ${({ theme }) => theme.text.standard};
      background-color: ${({ theme }) => alpha(theme.background.standard, 1)};
    }
  }

  #method {
    color: ${({ theme, methodColor }) => methodColor || theme.primary};
    margin-right: 0.75rem;
    font-size: 1.6ch;
  }

  padding-bottom: 1.5rem;
`;

const CodeExampleContainer = styled.div`
  width: 100%;
`;

function convertToDataFormat(input: any[]): string {
  const dataObject: { [key: string]: string } = {};

  if (input.length === 0) return '';

  input.forEach((item) => {
    dataObject[item.name] = item.value || 'example_val';
  });

  const dataString = JSON.stringify(dataObject, null, 3);
  return ` --data '${dataString.replace('}', ' }')}'`;
}

export const WorkflowExecuteInfo: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const theme = useTheme();

  const { service_id, workflow_id } = useParams();

  const open = useSelector((state: State) => state.flow.viewExecuteInfo);
  const setOpen = (val: boolean) => dispatch(flowActions.setViewExecuteInfo(val));

  const [copied, setCopied] = useState(false);

  const commands = useSelector((state: State) => state.tabs.active.commands);
  const triggerCommand = commands.find((item) => item.config.trigger);
  const path = useSelector((state: State) => state.tabs.active.service_path);

  const executableFlowPath = !!workflow_id ? workflow_id : path;

  const copyEndpoint = () => {
    copyToClipboard(`${API_BASE_NO_API}/execute/${service_id}/${executableFlowPath}`);
    setCopied(true);

    setTimeout(() => setCopied(false), 2000);
  };

  if (triggerCommand?.config.command_id === 'workflow.module') {
    return (
      <Modal
        open={Boolean(open)}
        background='black'
        setOpen={setOpen}
        header='Execute Instructions'
        icon={<CodeOutlined />}
        body={
          <Container>
            <Alert severity='info'>
              Modules are a special type of workflow that gets executed using the Module Node. You can use modules to
              encapuslate a set of reusable functionality that can be called from other workflows. Once youve finished
              creating your module, go to another workflow and right click the canvas to view your new node!
            </Alert>
          </Container>
        }
        actions={
          <Stack direction='row' spacing={1}>
            <Button
              small
              onClick={() => {
                setOpen(false);
              }}
            >
              Close
            </Button>
          </Stack>
        }
      />
    );
  }

  if (triggerCommand?.config.command_id === 'call.initiated') {
    return (
      <Modal
        open={Boolean(open)}
        background='black'
        setOpen={setOpen}
        header='Execute Instructions'
        icon={<CodeOutlined />}
        body={
          <Stack spacing={1}>
            <Alert severity='info' sx={{ whiteSpace: 'pre-wrap' }}>
              {`This workflow uses an inbound call trigger. This workflow will be triggered when you ring the number associated with the voice application assigned in the trigger node.\n\nYou can test your workflow by clicking the trigger node, followed by clicking "Run Workflow" in the top right corner of the sidebar.\n\nThe trigger node sidebar will list the numbers associated with your voice application. You can ring any of these numbers from your personal phone to trigger the workflow.`}
            </Alert>
          </Stack>
        }
        actions={
          <Stack direction='row' spacing={1}>
            <Button
              small
              onClick={() => {
                setOpen(false);
              }}
            >
              Close
            </Button>
          </Stack>
        }
      />
    );
  }

  if (triggerCommand?.config.command_id !== 'http.request') return null;

  const state = triggerCommand?.state;
  const method = state?.method;
  const query_parameters = state?.query_parameters;
  const body_parameters = state?.body_parameters;

  const queryParameters =
    query_parameters?.length > 0
      ? `?${query_parameters.map((item) => `${item.name}=${item.value || 'example_val'}`).join('&')}`
      : '';

  const curlExample = [
    '```bash',
    `curl --request ${method.toUpperCase()} ${API_BASE_NO_API}/execute/${service_id}/${executableFlowPath}${queryParameters} \\`,
    ` --header "Content-Type: application/json" \\`,
    ` --header "Authorization: Bearer YOUR_API_KEY" ${
      body_parameters.length > 0 && method !== 'get' && method !== 'delete' ? '\\' : ''
    }`,
    method !== 'get' && method !== 'delete' ? convertToDataFormat(body_parameters) : '',
    '```',
  ].join('\n');

  const methodColor = () => {
    if (method === 'get') return theme.telnyx.green;
    if (method === 'put' || method === 'post' || method === 'patch') return theme.telnyx.blue;
    if (method === 'delete') return theme.telnyx.red;
  };

  return (
    <Modal
      open={Boolean(open)}
      background='black'
      setOpen={setOpen}
      header='Execute Instructions'
      icon={<CodeOutlined />}
      body={
        <Container>
          <Stack spacing={1} width='100%'>
            <Label title='Endpoint Path' tooltip='Sending a HTTP request to this path will execute your workflow.' />
            <CopyEndpointButton methodColor={methodColor()}>
              <ListItemButton
                onClick={copyEndpoint}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                }}
              >
                <Stack direction='row' alignItems='center'>
                  <strong id='method'>{method.toUpperCase()}</strong>
                  {`${API_BASE_NO_API}/execute/${service_id}/${executableFlowPath}`}
                </Stack>
                {copied ? <CheckOutlined /> : <ContentCopyRounded />}
              </ListItemButton>
            </CopyEndpointButton>
          </Stack>
          <Stack width='100%'>
            <Label
              title='cURL Example'
              tooltip='This is an example of how you can execute your endpoint using cURL. You can paste this command into any bash terminal and hit enter.'
            />
            <CodeExampleContainer>
              <Markdown content={curlExample} />
            </CodeExampleContainer>
          </Stack>
        </Container>
      }
      actions={
        <Stack direction='row' spacing={1}>
          <Button
            small
            onClick={() => {
              setOpen(false);
            }}
          >
            Close
          </Button>
        </Stack>
      }
    />
  );
};
